import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import useLenis from "@/hooks/useLenis";
import useSEO from "@/hooks/useSEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { HOME } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAM_DATA = {
  ascent: {
    n: "01",
    title: "Ascent",
    duration: "12 Weeks",
    focus: "Hypertrophy · Zone 2",
    tagline: "Wake the fascia. Rebuild the tendons. Find the shape.",
    intro:
      "Ascent is a twelve-week gradient of load and stillness. It rewires the connective tissue, unlocks the hips, and re-teaches you how to breathe under a barbell. Written for the athlete returning to iron after seasons away — or for the deskbound who is ready to be dangerous again.",
    schedule: [
      ["01–02", "Foundation", "Range · Tempo · Zone 2 base"],
      ["03–06", "Accumulation", "Hypertrophy blocks · 60-min z2 flushes"],
      ["07–10", "Intensification", "Heavy triples · Threshold work"],
      ["11–12", "Peak & Test", "Realisation · Full body benchmarks"],
    ],
  },
  altitude: {
    n: "02",
    title: "Altitude",
    duration: "8 Weeks",
    focus: "VO₂ · Breathwork",
    tagline: "Thin the air in your own lungs.",
    intro:
      "Altitude is a compact eight-week protocol built to raise your ceiling. Zone-5 intervals, nasal-breathing drills, and cold exposure form a triad that expands mitochondrial density and mental composure. Wear a heart-rate strap. Bring humility.",
    schedule: [
      ["01–02", "Nasal Base", "z2 runs, box breathing 4-4-4-4"],
      ["03–05", "Interval Block", "4×4s at 92% HRmax · CO₂ tolerance"],
      ["06–07", "Ceiling Test", "5k time trial · plunge protocol"],
      ["08", "Recovery Week", "Mobility · sauna · long slow"],
    ],
  },
  stillness: {
    n: "03",
    title: "Stillness",
    duration: "Perennial",
    focus: "Mobility · Meditation",
    tagline: "Twenty minutes of dawn before the world remembers you exist.",
    intro:
      "Stillness is not a program. It is a posture — a daily twenty-minute ritual that runs alongside every other program you own. Movement, breath, silence. Practiced correctly, it doubles the return on everything else you do.",
    schedule: [
      ["Daily", "05:00–05:20", "Breath ladder · joint circles"],
      ["Daily", "05:20–05:35", "Silent seated · one thought held"],
      ["Weekly", "Sunday AM", "45-min mobility flow · journaling"],
      ["Monthly", "New Moon", "Fasted long-walk in silence"],
    ],
  },
  "iron-order": {
    n: "04",
    title: "Iron Order",
    duration: "16 Weeks",
    focus: "Strength · Power",
    tagline: "Barbell only. No music. Signed by the numbered.",
    intro:
      "Iron Order is a sixteen-week strength discipline for those who want the barbell to become a second language. Squat. Bench. Deadlift. Overhead press. No noise, no accessories that don't earn their place. You end the block heavier, quieter, and unfamiliar with excuses.",
    schedule: [
      ["01–04", "Rebuild", "5×5 · sub-max · technical grooving"],
      ["05–08", "Volume", "5/3/1 · back-off sets · GPP"],
      ["09–12", "Intensification", "Doubles · singles · pause work"],
      ["13–16", "Peak", "Openers · attempts · silent test"],
    ],
  },
};

const NotFound = () => (
  <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 text-center">
    <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-3">
      §—— UNMAPPED
    </div>
    <h1 className="font-serif italic text-4xl md:text-6xl text-white mb-6">
      This road is not marked.
    </h1>
    <Link
      to="/#programs"
      className="text-[11px] tracking-[0.3em] uppercase text-silver-100 hover:text-white"
    >
      ← Back to Programs
    </Link>
  </div>
);

const EnrollForm = ({ slug, title }) => {
  const [form, setForm] = useState({ name: "", email: "", goal: "" });
  const [status, setStatus] = useState("idle");
  const [numberedId, setNumberedId] = useState("");
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      toast.error("Name and a valid email are required.");
      return;
    }
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/enrollments`, {
        program_slug: slug,
        name: form.name,
        email: form.email,
        goal: form.goal,
      });
      setNumberedId(res.data?.numbered_id || "");
      setStatus("success");
      toast.success(`Enrolled in ${title}. ${res.data?.numbered_id || ""}`);
    } catch (err) {
      setStatus("idle");
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Enrollment failed.";
      toast.error(typeof msg === "string" ? msg : "Enrollment failed.");
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-silver-100/40 rounded-md p-8 md:p-10 bg-white/[0.03]"
      >
        <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-3">
          §— ENROLLED
        </div>
        <div className="font-serif italic text-3xl md:text-4xl text-silver-100 mb-3">
          Welcome, {form.name.split(" ")[0] || "warrior"}.
        </div>
        <p className="text-white/65 leading-[1.75] max-w-md">
          Your number has been sealed. Watch your inbox at dawn — the first
          dispatch and login details arrive shortly.
        </p>
        <div className="mt-6 font-mono text-[11px] tracking-[0.36em] text-white">
          YOUR NUMBER — {numberedId}
        </div>
        <button
          onClick={() => navigate("/")}
          className="mt-8 inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-white/70 hover:text-white"
        >
          Return home
          <span className="w-8 h-px bg-silver-100" />
        </button>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={HOME.enrollmentForm}
      className="border border-white/10 rounded-md p-8 md:p-10 bg-white/[0.02] space-y-6"
    >
      <div>
        <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-2">
          §— ENROLL
        </div>
        <h3 className="font-serif text-white text-3xl md:text-4xl">
          Sign into <span className="italic text-silver-100">{title}</span>
        </h3>
        <p className="text-white/55 text-[13px] mt-2 leading-[1.7]">
          The council reviews new numbers weekly. No cards required to enlist.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <label className="block">
          <span className="font-mono text-[10px] tracking-[0.35em] text-silver-300">
            NAME
          </span>
          <input
            required
            type="text"
            data-testid={HOME.enrollmentName}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-silver-100 outline-none text-white py-2 text-[15px]"
            placeholder="Your full name"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] tracking-[0.35em] text-silver-300">
            EMAIL
          </span>
          <input
            required
            type="email"
            data-testid={HOME.enrollmentEmail}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-silver-100 outline-none text-white py-2 text-[15px]"
            placeholder="you@dispatch.com"
          />
        </label>
      </div>

      <label className="block">
        <span className="font-mono text-[10px] tracking-[0.35em] text-silver-300">
          WHY THIS PROGRAM?
        </span>
        <textarea
          data-testid={HOME.enrollmentGoal}
          value={form.goal}
          onChange={(e) => setForm({ ...form, goal: e.target.value })}
          rows={3}
          className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-silver-100 outline-none text-white py-2 text-[15px] resize-none"
          placeholder="One line. Optional."
        />
      </label>

      <button
        type="submit"
        data-testid={HOME.enrollmentSubmit}
        disabled={status === "loading"}
        className="inline-flex items-center gap-3 px-6 py-4 rounded-full bg-silver-100 text-black hover:bg-white transition-colors duration-500 disabled:opacity-60"
      >
        <span className="text-[11px] tracking-[0.28em] uppercase font-medium">
          {status === "loading" ? "Sealing your number…" : "Enroll — receive number"}
        </span>
        <span className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 6H11M11 6L6 1M11 6L6 11" stroke="currentColor" strokeWidth="1.2" />
          </svg>
        </span>
      </button>
    </form>
  );
};

const ProgramDetail = () => {
  useLenis();
  const { slug } = useParams();
  const program = PROGRAM_DATA[slug];

  useSEO({
    title: program
      ? `${program.title} · ${program.duration} · Mind. Body. Soul. Programs`
      : "Program · Mind. Body. Soul.",
    description: program
      ? `${program.tagline} — ${program.focus}. ${program.duration}. Enroll now.`
      : "Programs by Mind. Body. Soul. — Ascent, Altitude, Stillness, Iron Order.",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <div className="relative bg-black text-white min-h-screen">
      <Header />
      <main className="pt-32 md:pt-40">
        {!program ? (
          <NotFound />
        ) : (
          <>
            {/* Hero */}
            <section className="relative px-6 md:px-10 pb-16 md:pb-24">
              <div className="max-w-[1400px] mx-auto">
                <Link
                  to="/#programs"
                  className="inline-flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-white/50 hover:text-white transition-colors mb-10"
                >
                  <span className="w-6 h-px bg-silver-100" />
                  Back to Programs
                </Link>

                <div className="grid grid-cols-12 gap-6 items-end border-b border-white/10 pb-10">
                  <div className="col-span-12 md:col-span-8">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="font-mono text-[10px] tracking-[0.42em] text-silver-300">
                        §PROGRAM · N° {program.n}
                      </span>
                      <span className="inline-flex items-center gap-2 px-2.5 py-1 border border-silver-100/40 rounded-full font-mono text-[9px] tracking-[0.32em] uppercase text-silver-100">
                        {program.duration}
                      </span>
                    </div>
                    <h1 className="font-serif text-white text-[16vw] md:text-[10vw] leading-[0.9] tracking-[-0.03em]">
                      {program.title}
                    </h1>
                    <p className="mt-6 font-serif italic text-silver-100 text-2xl md:text-3xl">
                      {program.tagline}
                    </p>
                  </div>
                  <div className="col-span-12 md:col-span-4 md:text-right">
                    <div className="font-mono text-[10px] tracking-[0.35em] text-silver-300 mb-2">
                      FOCUS
                    </div>
                    <div className="text-white text-lg tracking-[0.14em] uppercase">
                      {program.focus}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Body */}
            <section className="relative px-6 md:px-10 pb-20 md:pb-28">
              <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-8 md:gap-14">
                {/* Intro + Schedule */}
                <div className="col-span-12 md:col-span-7">
                  <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-4">
                    §01 — THE PROTOCOL
                  </div>
                  <p className="text-white/75 leading-[1.85] text-[16px] md:text-[17px] max-w-2xl">
                    {program.intro}
                  </p>

                  <div className="mt-14">
                    <div className="font-mono text-[10px] tracking-[0.42em] text-silver-300 mb-6">
                      §02 — SCHEDULE
                    </div>
                    <div className="space-y-3">
                      {program.schedule.map(([wk, phase, detail], i) => (
                        <motion.div
                          key={wk}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true, margin: "-40px" }}
                          transition={{
                            duration: 0.7,
                            delay: i * 0.08,
                            ease: [0.22, 1, 0.36, 1],
                          }}
                          className="grid grid-cols-12 gap-4 py-4 border-b border-white/10"
                        >
                          <div className="col-span-3 md:col-span-2 font-mono text-silver-200 text-sm tracking-[0.22em]">
                            {wk}
                          </div>
                          <div className="col-span-4 md:col-span-3 font-serif italic text-white text-lg">
                            {phase}
                          </div>
                          <div className="col-span-5 md:col-span-7 text-white/60 text-[14px] leading-[1.6]">
                            {detail}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Enroll */}
                <div className="col-span-12 md:col-span-5">
                  <EnrollForm slug={slug} title={program.title} />
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default ProgramDetail;
